'use client';
import { useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { useMarketTradeModal } from '@/hooks/shared/useMarketTradeModal';
import { useUrlSearchSync } from '@/hooks/shared/useUrlSearchSync';
import { useCategories, useMarketsInfinite, useTagsBySlug } from '@/services/query';
import type { EventMarket, EventOutcome, Market, Outcome } from '@/types';
import { useHomeFilters } from './useHomeFilters';

import { PAGINATION } from '@/constants';

// ==========================================
// Types
// ==========================================

type TradeModalState = {
  isOpen: boolean;
  market: Market | EventMarket | null;
  outcome: Outcome | EventOutcome | null;
  isBuyOrder: boolean;
  forceDesktopVersion?: boolean;
};

// ==========================================
// Constants
// ==========================================

/**
 * Custom hook to manage the state and logic for the Home page.
 * Handles:
 * - Route-based category selection
 * - Data fetching (Markets, Categories, Tags) with infinite scroll
 * - Filtering and Sorting state
 * - UI state (Modals, Dropdowns)
 * - Trading interactions
 */
export const useHome = (overrideCategory?: string) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Determine active category from URL or override.
  const rawSlug = pathname === '/' ? 'all' : pathname.replace('/', '');
  let activeCategorySlug = overrideCategory || rawSlug;

  if (activeCategorySlug === 'trending') {
    activeCategorySlug = 'all';
  }

  // --- 1. Shared Infrastructure ---
  const { searchQuery, debouncedSearchQuery, handleSearchChange, handleSearchSubmit } =
    useUrlSearchSync();

  const {
    sortBy,
    setSortBy,
    frequency,
    setFrequency,
    status,
    setStatus,
    selectedTag,
    hideSport,
    setHideSport,
    sortParams,
    handleTagClick,
    handleResetFilters,
  } = useHomeFilters();

  const { tradeModalState, handleOpenTradeModal, handleCloseTradeModal } = useMarketTradeModal();

  // --- 2. Local UI State ---
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(
    searchParams.has('sort') || searchParams.has('freq') || searchParams.has('status')
  );

  const moreButtonRef = useRef<HTMLButtonElement>(null);

  // --- 3. Business Logic Helpers ---
  const getTrendingFilters = () => {
    const isNewCategory = activeCategorySlug.toLowerCase() === 'new' && !selectedTag;
    const isTrendingPage = pathname === '/' || rawSlug === 'trending';
    const isSortNewest = sortBy === 'Newest';
    const isResolved = status === 'Closed' || status === 'Resolved';

    const filters: any = {};
    if (isNewCategory || (isTrendingPage && isSortNewest) || isResolved) {
      filters.exclude_tag_id = [102169, 100639];
      filters.archived = false;
    }
    return filters;
  };

  // --- 4. Data Fetching ---
  const { isLoading: isCategoriesLoading, refetch: refetchCategories } = useCategories();
  const { data: tagsData, isLoading: isTagsLoading } = useTagsBySlug(activeCategorySlug);

  const {
    data,
    isLoading: isMarketsLoading,
    error,
    refetch: refetchMarkets,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMarketsInfinite({
    category: activeCategorySlug,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...sortParams,
    status: status.toLowerCase() as any,
    active: status === 'Active' || status === 'Closed' || status === 'Resolved',
    closed: status === 'Closed' || status === 'Resolved',
    tag_slug:
      selectedTag ||
      (activeCategorySlug !== 'all' && activeCategorySlug.toLowerCase() !== 'new'
        ? activeCategorySlug
        : ''),
    ascending: true,
    recurrence: frequency !== 'All' ? frequency.toLowerCase() : undefined,
    archived: false,
    search: debouncedSearchQuery || undefined,
    hideSport: hideSport,
    ...getTrendingFilters(),
  });

  const events = data?.pages.flatMap((page) => page.items) || [];
  const isLoading = isMarketsLoading || isCategoriesLoading || isTagsLoading;

  // --- 5. Interaction Handlers ---
  const toggleMoreDropdown = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsMoreDropdownOpen((prev) => !prev);
  };

  const handleTradeClick = (marketId: string, outcomeId: string, isBuyOrder: boolean) => {
    const foundMarket = events.flatMap((ev) => ev.markets || []).find((m) => m.id === marketId);
    if (!foundMarket) return;

    let outcomes: any[] = [];
    try {
      outcomes =
        typeof foundMarket.outcomes === 'string'
          ? JSON.parse(foundMarket.outcomes)
          : foundMarket.outcomes;
    } catch {
      outcomes = [];
    }

    const foundOutcome = Array.isArray(outcomes) ? outcomes.find((o) => o.id === outcomeId) : null;
    if (!foundOutcome) return;

    handleOpenTradeModal(foundMarket as any, foundOutcome as any, isBuyOrder);
  };

  return {
    state: {
      isMoreDropdownOpen,
      activeCategorySlug,
      tradeModalState,
      searchQuery,
      isFilterOpen,
      sortBy,
      frequency,
      status,
      events,
      isLoading,
      tags: tagsData,
      isTagsLoading,
      selectedTag,
      hideSport,
      error,
    },
    refs: {
      moreButtonRef,
    },
    handlers: {
      setSortBy,
      setFrequency,
      setStatus,
      setHideSport,
      toggleMoreDropdown,
      handleSearchChange,
      handleSearchSubmit,
      handleFilterToggle: () => setIsFilterOpen((p) => !p),
      handleTradeClick,
      handleTradeModalClose: handleCloseTradeModal,
      handleSportsMatchTradeClick: () => {},
      handleTagClick,
      handleResetFilters,
      refetch: () => {
        refetchCategories();
        refetchMarkets();
      },
      fetchNextPage,
    },
    pagination: {
      hasNextPage,
      isFetchingNextPage,
    },
  };
};

export type UseHomeReturn = ReturnType<typeof useHome>;
